import type { IBadge, ITreeNode } from './types';

const TONE_COLOR: Record<NonNullable<IBadge['tone']>, string> = {
  neutral: 'default',
  info: 'blue',
  success: 'green',
  warning: 'orange',
  danger: 'red',
};

export const getTagColor = (tag: IBadge) =>
  tag.color || TONE_COLOR[tag.tone ?? 'neutral'];

/** Distinct tags of the whole tree, keyed by label (first occurrence wins). */
export const collectTags = (nodes: ITreeNode[]): IBadge[] => {
  const byLabel = new Map<string, IBadge>();
  const walk = (list: ITreeNode[]) =>
    list.forEach(node => {
      node.tags?.forEach(tag => {
        if (!byLabel.has(tag.label)) byLabel.set(tag.label, tag);
      });
      if (node.children) walk(node.children);
    });
  walk(nodes);
  return [...byLabel.values()];
};

/**
 * Labels of the enabled tags, or `null` when filtering should not apply
 * (all tags on). An empty set means all tags are off.
 */
export const getEnabledTags = (
  tags: IBadge[],
  disabled: Set<string>
): Set<string> | null =>
  disabled.size === 0
    ? null
    : new Set(
        tags.filter(tag => !disabled.has(tag.label)).map(tag => tag.label)
      );

/** With all tags off (`enabled` is empty) only untagged nodes match. */
export const tagFilterMatches = (
  node: ITreeNode,
  enabled: Set<string> | null
): boolean => {
  if (!enabled) return true;
  const selfMatches = enabled.size
    ? !!node.tags?.some(tag => enabled.has(tag.label))
    : !node.tags?.length;
  return (
    selfMatches ||
    !!node.children?.some(child => tagFilterMatches(child, enabled))
  );
};
